import {
  MESSENGER_LABELS,
  type MessengerStatus,
  type ProductImportResult,
  type ProductItem,
  STAGE_COLORS,
  STAGE_SEMANTIC_LABELS,
  STAGE_SEMANTICS,
  type StageSemantic,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, ChevronLeft, Plus, Search, Trash2, Upload } from 'lucide-react';
import { type FormEvent, useRef, useState } from 'react';
import { ActivityRow } from '@/components/crm-activities';
import { ChannelMark, CrmNav, EmptyPanel, Money, Pager } from '@/components/crm-bits';
import { ChatPanel } from '@/components/crm-chat';
import { Dialog } from '@/components/dialog';
import {
  Badge,
  Button,
  ErrorBox,
  Field,
  Input,
  PageHeader,
  Segmented,
  Spinner,
  Textarea,
} from '@/components/ui';
import { UserPicker } from '@/components/user-picker';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import {
  activitiesQuery,
  chatQuery,
  chatsQuery,
  crmMetaQuery,
  messengersQuery,
  productsQuery,
} from '@/lib/crm';
import { formatMoment } from '@/lib/format';

// ---------- Чаты ----------

export interface ChatsSearch {
  chat?: string;
  unread?: boolean;
  q?: string;
}

export function ChatsPage() {
  const search = useSearch({ strict: false }) as ChatsSearch;
  const navigate = useNavigate();
  const [q, setQ] = useState(search.q ?? '');
  const t = useRef<number | undefined>(undefined);
  const list = useQuery(chatsQuery({ unread: search.unread, q: search.q }));
  const setSearch = (patch: Partial<ChatsSearch>) =>
    navigate({ to: '/crm/chats', search: { ...search, ...patch }, replace: true });
  const selected = search.chat;

  return (
    <div>
      <PageHeader title="CRM" description="Переписка с клиентами через ботов компании в Telegram и MAX" />
      <CrmNav />
      <div className="grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
        <section
          className={clsx('flex min-w-0 flex-col gap-3', selected && 'hidden lg:flex')}
          aria-label="Диалоги"
        >
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              label="Фильтр"
              value={search.unread ? 'unread' : 'all'}
              onChange={(v) => setSearch({ unread: v === 'unread' || undefined })}
              options={[
                { value: 'all', label: 'Все' },
                { value: 'unread', label: 'Непрочитанные' },
              ]}
            />
            <div className="relative min-w-[160px] flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
                aria-hidden
              />
              <Input
                type="search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  window.clearTimeout(t.current);
                  const v = e.target.value.trim() || undefined;
                  t.current = window.setTimeout(() => setSearch({ q: v }), 250);
                }}
                placeholder="Имя или @ник"
                aria-label="Поиск диалогов"
                className="pl-9"
              />
            </div>
          </div>
          {list.isPending ? (
            <Spinner />
          ) : list.error ? (
            <ErrorBox error={list.error} />
          ) : list.data.length === 0 ? (
            <EmptyPanel title="Диалогов нет">
              Когда клиент напишет боту компании, диалог появится здесь.
            </EmptyPanel>
          ) : (
            <ul className="panel divide-y divide-line overflow-hidden">
              {list.data.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSearch({ chat: c.id })}
                    aria-current={selected === c.id ? 'true' : undefined}
                    className={clsx(
                      'flex w-full gap-3 px-3 py-2.5 text-left',
                      selected === c.id ? 'bg-sunken' : 'hover:bg-paper',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5">
                        <ChannelMark channel={c.channel} />
                        <span className={clsx('min-w-0 truncate', c.unread > 0 && 'font-semibold')}>
                          {c.contact?.fullName ?? c.title}
                        </span>
                      </p>
                      <p className="mt-0.5 truncate text-[13px] text-steel">
                        {c.lastDirection === 'out' && 'Вы: '}
                        {c.lastMessagePreview ?? '—'}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="text-[11.5px] text-steel">
                        {c.lastMessageAt ? formatMoment(c.lastMessageAt) : ''}
                      </span>
                      {c.unread > 0 && (
                        <span className="min-w-[20px] rounded-full bg-danger px-1.5 text-center text-[11.5px] font-semibold leading-[18px] text-white tabular-nums">
                          {c.unread}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        <div className={clsx('min-w-0', !selected && 'hidden lg:block')}>
          {selected ? (
            <SelectedChat id={selected} onBack={() => setSearch({ chat: undefined })} />
          ) : (
            <div className="panel flex h-[520px] items-center justify-center text-steel">
              Выберите диалог слева
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SelectedChat({ id, onBack }: { id: string; onBack: () => void }) {
  const chat = useQuery(chatQuery(id));
  if (chat.isPending) return <Spinner />;
  if (chat.error) return <ErrorBox error={chat.error} />;
  const c = chat.data;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13.5px]">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-steel hover:text-ink lg:hidden"
        >
          <ChevronLeft className="size-4" aria-hidden />
          Диалоги
        </button>
        {c.contact && (
          <Link to="/crm/contacts/$id" params={{ id: c.contact.id }} className="link">
            Карточка клиента
          </Link>
        )}
        {c.openDeal ? (
          <Link to="/crm/deals/$id" params={{ id: c.openDeal.id }} className="link">
            Сделка #{c.openDeal.number} {c.openDeal.title}
          </Link>
        ) : (
          <span className="text-steel">открытой сделки нет — следующее сообщение клиента создаст новую</span>
        )}
      </div>
      <ChatPanel chat={c} className="h-[calc(100dvh-260px)] min-h-[420px]" />
    </div>
  );
}

// ---------- Дела ----------

export interface ActivitiesSearch {
  state?: 'done';
  scope?: 'all';
}

export function ActivitiesPage() {
  const search = useSearch({ strict: false }) as ActivitiesSearch;
  const navigate = useNavigate();
  const can = useCan();
  const q = useQuery(activitiesQuery({ state: search.state ?? 'open', scope: search.scope ?? 'mine' }));
  const setSearch = (patch: Partial<typeof search>) =>
    navigate({ to: '/crm/activities', search: { ...search, ...patch }, replace: true });
  const items = q.data ?? [];
  const now = new Date();
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
  const groups =
    search.state === 'done'
      ? [{ title: 'Выполненные', items }]
      : [
          { title: 'Просрочены', items: items.filter((a) => a.isOverdue) },
          {
            title: 'Сегодня',
            items: items.filter((a) => !a.isOverdue && new Date(a.dueAt).getTime() < endOfDay),
          },
          { title: 'Дальше', items: items.filter((a) => new Date(a.dueAt).getTime() >= endOfDay) },
        ];
  return (
    <div>
      <PageHeader
        title="CRM"
        description="Звонки, встречи и напоминания по клиентам. Дела ставятся в карточке сделки или клиента."
      />
      <CrmNav />
      <div className="mb-4 flex flex-wrap gap-3">
        <Segmented
          label="Состояние"
          value={search.state ?? 'open'}
          onChange={(v) => setSearch({ state: v === 'done' ? 'done' : undefined })}
          options={[
            { value: 'open', label: 'Запланированные' },
            { value: 'done', label: 'Выполненные' },
          ]}
        />
        {can('crm.deal.read_all') && (
          <Segmented
            label="Чьи дела"
            value={search.scope ?? 'mine'}
            onChange={(v) => setSearch({ scope: v === 'all' ? 'all' : undefined })}
            options={[
              { value: 'mine', label: 'Мои' },
              { value: 'all', label: 'Мои и сотрудников' },
            ]}
          />
        )}
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : items.length === 0 ? (
        <EmptyPanel title={search.state === 'done' ? 'Выполненных дел нет' : 'Запланированных дел нет'} />
      ) : (
        <div className="flex max-w-[860px] flex-col gap-5">
          {groups
            .filter((g) => g.items.length)
            .map((g) => (
              <section key={g.title}>
                <h2
                  className={clsx(
                    'mb-1.5 text-[13.5px] font-semibold',
                    g.title === 'Просрочены' && 'text-danger',
                  )}
                >
                  {g.title} <span className="font-normal tabular-nums text-steel">{g.items.length}</span>
                </h2>
                <ul className="panel divide-y divide-line">
                  {g.items.map((a) => (
                    <ActivityRow key={a.id} a={a} showLinks />
                  ))}
                </ul>
              </section>
            ))}
        </div>
      )}
    </div>
  );
}

// ---------- Товары ----------

export interface ProductsSearch {
  q?: string;
  page?: number;
  inactive?: boolean;
}

export function ProductsPage() {
  const search = useSearch({ strict: false }) as ProductsSearch;
  const navigate = useNavigate();
  const can = useCan();
  const [q, setQ] = useState(search.q ?? '');
  const t = useRef<number | undefined>(undefined);
  const [editing, setEditing] = useState<ProductItem | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const list = useQuery(
    productsQuery({ q: search.q, page: search.page ?? 1, includeInactive: search.inactive }),
  );
  const setSearch = (patch: Partial<typeof search>) =>
    navigate({ to: '/crm/products', search: { ...search, page: undefined, ...patch }, replace: true });
  const manage = can('crm.product.manage');
  return (
    <div>
      <PageHeader
        title="CRM"
        description={list.data ? `Товаров и услуг в справочнике: ${list.data.total}` : undefined}
        actions={
          manage && (
            <>
              <Button variant="secondary" onClick={() => setImporting(true)}>
                <Upload className="size-4" aria-hidden />
                Загрузить CSV
              </Button>
              <Button onClick={() => setEditing('new')}>
                <Plus className="size-4" aria-hidden />
                Товар
              </Button>
            </>
          )
        }
      />
      <CrmNav />
      {editing && (
        <ProductDialog product={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />
      )}
      {importing && <ImportDialog onClose={() => setImporting(false)} />}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-[320px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
            aria-hidden
          />
          <Input
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              window.clearTimeout(t.current);
              const v = e.target.value.trim() || undefined;
              t.current = window.setTimeout(() => setSearch({ q: v }), 250);
            }}
            placeholder="Артикул или название"
            aria-label="Поиск товаров"
            className="pl-9"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-[13.5px]">
          <input
            type="checkbox"
            checked={Boolean(search.inactive)}
            onChange={(e) => setSearch({ inactive: e.target.checked || undefined })}
            className="size-4 accent-[var(--color-graphite)]"
          />
          Показать снятые с продажи
        </label>
      </div>
      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : list.data.items.length === 0 ? (
        <EmptyPanel title="Товаров не найдено">
          {manage && 'Добавьте вручную или загрузите выгрузку из МойСклада или Битрикса в формате CSV.'}
        </EmptyPanel>
      ) : (
        <>
          <div className="panel overflow-x-auto">
            <table className="w-full min-w-[620px] text-left">
              <thead>
                <tr className="border-b border-line text-[13px] text-steel">
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    Артикул
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    Наименование
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-right font-medium">
                    Цена
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    Ед.
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {list.data.items.map((p) => (
                  <tr key={p.id} className="hover:bg-paper/70">
                    <td className="px-4 py-2.5 tabular-nums text-ink-soft">{p.sku ?? '—'}</td>
                    <td className="px-4 py-2.5">
                      {manage ? (
                        <button
                          type="button"
                          onClick={() => setEditing(p)}
                          className="text-left hover:text-link"
                        >
                          {p.name}
                        </button>
                      ) : (
                        p.name
                      )}
                      {!p.isActive && (
                        <span className="ml-2">
                          <Badge>снят</Badge>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Money amount={p.price} />
                    </td>
                    <td className="px-4 py-2.5 text-ink-soft">{p.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={list.data.page}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => setSearch({ page: p })}
          />
        </>
      )}
    </div>
  );
}

function ProductDialog({ product, onClose }: { product?: ProductItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      product
        ? api(`/crm/products/${product.id}`, { method: 'PATCH', body })
        : api('/crm/products', { method: 'POST', body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['crm'] });
      onClose();
    },
    onError: (err) =>
      err instanceof ApiError &&
      setErrors({ ...err.fields, _: Object.keys(err.fields).length ? '' : err.message }),
  });
  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    save.mutate({
      sku: String(fd.get('sku') ?? ''),
      name: String(fd.get('name') ?? ''),
      unit: String(fd.get('unit') ?? 'шт'),
      price:
        String(fd.get('price') ?? '0')
          .replace(/\s/g, '')
          .replace(',', '.') || 0,
      isActive: fd.get('isActive') === 'on',
    });
  }
  return (
    <Dialog title={product ? 'Товар' : 'Новый товар'} onClose={onClose} width={560}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 px-5 py-5">
        <Field label="Наименование" error={errors.name}>
          {(p) => <Input {...p} name="name" defaultValue={product?.name} />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Артикул" error={errors.sku}>
            {(p) => <Input {...p} name="sku" defaultValue={product?.sku ?? ''} />}
          </Field>
          <Field label="Цена, ₽" error={errors.price}>
            {(p) => <Input {...p} name="price" inputMode="decimal" defaultValue={product?.price ?? ''} />}
          </Field>
          <Field label="Единица" error={errors.unit}>
            {(p) => <Input {...p} name="unit" defaultValue={product?.unit ?? 'шт'} />}
          </Field>
        </div>
        <label className="inline-flex items-center gap-2 text-[13.5px]">
          <input
            type="checkbox"
            name="isActive"
            defaultChecked={product?.isActive ?? true}
            className="size-4 accent-[var(--color-graphite)]"
          />
          В продаже
        </label>
        {errors._ && <p className="text-[13.5px] text-danger">{errors._}</p>}
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={save.isPending}>
            Сохранить
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ImportDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const run = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api<ProductImportResult>('/crm/products/import', { method: 'POST', body: fd });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm'] }),
  });
  return (
    <Dialog title="Загрузка товаров из CSV" onClose={onClose} width={560}>
      <div className="flex flex-col gap-4 px-5 py-5 text-[13.5px]">
        <p className="text-ink-soft">
          Подойдёт выгрузка из МойСклада, Битрикса или таблица Excel, сохранённая как «CSV». Нужны колонки
          «Наименование» и, по желанию, «Артикул», «Цена» (или «Цена продажи») и «Ед. изм.». Товар с тем же
          артикулом обновится, новый — добавится.
        </p>
        <input
          ref={input}
          type="file"
          accept=".csv,text/csv,text/plain"
          className="sr-only"
          onChange={(e) => e.target.files?.[0] && run.mutate(e.target.files[0])}
        />
        <Button
          variant="secondary"
          loading={run.isPending}
          onClick={() => input.current?.click()}
          className="self-start"
        >
          <Upload className="size-4" aria-hidden />
          Выбрать файл
        </Button>
        {run.error && <ErrorBox error={run.error} />}
        {run.data && (
          <div className="rounded-md bg-ok-soft px-3 py-2">
            <p className="font-medium text-ok">
              Добавлено: {run.data.created}, обновлено: {run.data.updated}
              {run.data.skipped ? `, пропущено строк: ${run.data.skipped}` : ''}
            </p>
            {run.data.errors.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-ink-soft">
                {run.data.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="flex justify-end border-t border-line pt-4">
          <Button variant="secondary" onClick={onClose}>
            Закрыть
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------- Настройки ----------

interface StageDraft {
  key: string;
  id?: string;
  name: string;
  color: string;
  semantic: StageSemantic;
}

export function CrmSettingsPage() {
  const meta = useQuery(crmMetaQuery);
  if (meta.isPending) return <Spinner />;
  if (meta.error) return <ErrorBox error={meta.error} />;
  return (
    <div>
      <PageHeader title="CRM" description="Стадии воронки, направления и подключение ботов" />
      <CrmNav />
      <div className="flex max-w-[920px] flex-col gap-6">
        <StagesEditor key={meta.dataUpdatedAt} />
        <DirectionsEditor key={`d${meta.dataUpdatedAt}`} />
        <InboxSettings key={`s${meta.dataUpdatedAt}`} />
        <Messengers />
      </div>
    </div>
  );
}

function StagesEditor() {
  const qc = useQueryClient();
  const meta = useQuery(crmMetaQuery);
  const [rows, setRows] = useState<StageDraft[]>(() =>
    (meta.data?.stages ?? []).map((s) => ({ ...s, key: s.id })),
  );
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api('/crm/settings/stages', {
        method: 'PUT',
        body: { stages: rows.map(({ id, name, color, semantic }) => ({ id, name, color, semantic })) },
      }),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await qc.invalidateQueries({ queryKey: ['crm'] });
    },
    onError: (err) => setError(err instanceof ApiError && err.fields._ ? err.fields._ : err.message),
  });
  const set = (key: string, patch: Partial<StageDraft>) => {
    setSaved(false);
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const moveRow = (i: number, d: -1 | 1) =>
    setRows((rs) => {
      const next = [...rs];
      const [row] = next.splice(i, 1);
      if (row) next.splice(i + d, 0, row);
      return next;
    });
  return (
    <section className="panel">
      <div className="border-b border-line px-5 py-3.5">
        <h2 className="font-semibold">Стадии сделок</h2>
        <p className="text-[13px] text-steel">
          Одна общая воронка. Новые сделки встают на первую стадию. «Успешная» и «Провал» закрывают сделку.
          Стадию со сделками удалить нельзя — сначала перенесите их.
        </p>
      </div>
      <ul className="divide-y divide-line">
        {rows.map((r, i) => (
          <li key={r.key} className="flex flex-wrap items-center gap-2 px-5 py-2">
            <div className="flex">
              <button
                type="button"
                aria-label="Выше"
                disabled={i === 0}
                onClick={() => moveRow(i, -1)}
                className="inline-flex size-8 items-center justify-center rounded text-steel hover:bg-sunken disabled:opacity-30"
              >
                <ArrowUp className="size-4" />
              </button>
              <button
                type="button"
                aria-label="Ниже"
                disabled={i === rows.length - 1}
                onClick={() => moveRow(i, 1)}
                className="inline-flex size-8 items-center justify-center rounded text-steel hover:bg-sunken disabled:opacity-30"
              >
                <ArrowDown className="size-4" />
              </button>
            </div>
            <input
              aria-label="Название стадии"
              className="input min-w-[160px] flex-1"
              value={r.name}
              onChange={(e) => set(r.key, { name: e.target.value })}
            />
            <select
              aria-label="Смысл стадии"
              className="input w-[150px]"
              value={r.semantic}
              onChange={(e) => set(r.key, { semantic: e.target.value as StageSemantic })}
            >
              {STAGE_SEMANTICS.map((s) => (
                <option key={s} value={s}>
                  {STAGE_SEMANTIC_LABELS[s]}
                </option>
              ))}
            </select>
            <fieldset className="flex gap-1">
              <legend className="sr-only">Цвет</legend>
              {STAGE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-pressed={r.color.toLowerCase() === c.toLowerCase()}
                  aria-label={c}
                  onClick={() => set(r.key, { color: c })}
                  className={clsx(
                    'size-5 rounded-full',
                    r.color.toLowerCase() === c.toLowerCase() && 'ring-2 ring-signal ring-offset-1',
                  )}
                  style={{ background: c }}
                />
              ))}
            </fieldset>
            <button
              type="button"
              aria-label="Удалить стадию"
              onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
              className="inline-flex size-8 items-center justify-center rounded text-steel hover:bg-sunken hover:text-danger"
            >
              <Trash2 className="size-4" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3 border-t border-line px-5 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const firstClosed = rows.findIndex((r) => r.semantic !== 'open');
            const draft: StageDraft = {
              key: crypto.randomUUID(),
              name: 'Новая стадия',
              color: STAGE_COLORS[1],
              semantic: 'open',
            };
            setRows((rs) =>
              firstClosed === -1
                ? [...rs, draft]
                : [...rs.slice(0, firstClosed), draft, ...rs.slice(firstClosed)],
            );
          }}
        >
          <Plus className="size-4" aria-hidden />
          Стадия
        </Button>
        <span className="flex-1" />
        {error && <span className="text-[13px] text-danger">{error}</span>}
        {saved && !error && <span className="text-[13px] text-ok">Сохранено</span>}
        <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить стадии
        </Button>
      </div>
    </section>
  );
}

function DirectionsEditor() {
  const qc = useQueryClient();
  const meta = useQuery(crmMetaQuery);
  const [rows, setRows] = useState(() =>
    (meta.data?.directions ?? []).map((d) => ({ ...d, key: d.id as string })),
  );
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api('/crm/settings/directions', {
        method: 'PUT',
        body: {
          directions: rows
            .filter((r) => r.name.trim())
            .map(({ id, name, isActive }) => ({ id: id || undefined, name, isActive })),
        },
      }),
    onSuccess: async () => {
      setSaved(true);
      await qc.invalidateQueries({ queryKey: ['crm'] });
    },
  });
  return (
    <section className="panel">
      <div className="border-b border-line px-5 py-3.5">
        <h2 className="font-semibold">Направления</h2>
        <p className="text-[13px] text-steel">
          Поле сделки и фильтр канбана. Выключенное направление остаётся у старых сделок.
        </p>
      </div>
      <ul className="divide-y divide-line">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-3 px-5 py-2">
            <input
              aria-label="Название направления"
              className="input flex-1"
              value={r.name}
              onChange={(e) => {
                setSaved(false);
                setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x)));
              }}
            />
            <label className="inline-flex shrink-0 items-center gap-2 text-[13.5px]">
              <input
                type="checkbox"
                checked={r.isActive}
                onChange={(e) => {
                  setSaved(false);
                  setRows((rs) =>
                    rs.map((x) => (x.key === r.key ? { ...x, isActive: e.target.checked } : x)),
                  );
                }}
                className="size-4 accent-[var(--color-graphite)]"
              />
              Действует
            </label>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-3 border-t border-line px-5 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            setRows((rs) => [...rs, { key: crypto.randomUUID(), id: '', name: '', isActive: true, sort: 0 }])
          }
        >
          <Plus className="size-4" aria-hidden />
          Направление
        </Button>
        <span className="flex-1" />
        {save.error && <span className="text-[13px] text-danger">{save.error.message}</span>}
        {saved && <span className="text-[13px] text-ok">Сохранено</span>}
        <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить направления
        </Button>
      </div>
    </section>
  );
}

function InboxSettings() {
  const qc = useQueryClient();
  const meta = useQuery(crmMetaQuery);
  const [owner, setOwner] = useState<string[]>(
    meta.data?.settings.inboxOwner ? [meta.data.settings.inboxOwner.userId] : [],
  );
  const [greeting, setGreeting] = useState(meta.data?.settings.greeting ?? '');
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api('/crm/settings', { method: 'PUT', body: { inboxOwnerId: owner[0] ?? null, greeting } }),
    onSuccess: async () => {
      setSaved(true);
      await qc.invalidateQueries({ queryKey: ['crm'] });
    },
  });
  return (
    <section className="panel">
      <div className="border-b border-line px-5 py-3.5">
        <h2 className="font-semibold">Новые обращения из мессенджеров</h2>
        <p className="text-[13px] text-steel">
          Новый человек написал боту — появляются клиент и сделка на первой стадии. Если клиент уже закреплён
          за менеджером — сделка достаётся ему.
        </p>
      </div>
      <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
        <Field
          label="Ответственный за новые обращения"
          hint="Пусто — сделка без ответственного: её видят все менеджеры, первый ответивший забирает себе"
        >
          {(p) => (
            <UserPicker
              id={p.id}
              value={owner}
              onChange={(v) => {
                setSaved(false);
                setOwner(v);
              }}
            />
          )}
        </Field>
        <Field
          label="Автоответ новому клиенту"
          hint="Отправляется один раз, на первое сообщение. Пусто — без автоответа"
        >
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={greeting}
              maxLength={1000}
              onChange={(e) => {
                setSaved(false);
                setGreeting(e.target.value);
              }}
              placeholder="Здравствуйте! Это ДизельПро. Менеджер ответит в рабочее время в течение 15 минут."
            />
          )}
        </Field>
      </div>
      <div className="flex items-center justify-end gap-3 border-t border-line px-5 py-3">
        {saved && <span className="text-[13px] text-ok">Сохранено</span>}
        <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить
        </Button>
      </div>
    </section>
  );
}

function MessengerRow({ s }: { s: MessengerStatus }) {
  return (
    <li className="flex flex-wrap items-start gap-3 px-5 py-3">
      <ChannelMark channel={s.channel} withLabel />
      <div className="min-w-0 flex-1 text-[13.5px]">
        {!s.configured ? (
          <p className="text-steel">
            Не подключён. Токен бота вписывается в файл <code className="rounded bg-sunken px-1">.env</code>{' '}
            на сервере ({s.channel === 'telegram' ? 'TELEGRAM_BOT_TOKEN' : 'MAX_BOT_TOKEN'}), затем перезапуск
            — инструкция в README.
          </p>
        ) : s.ok ? (
          <p>
            <span className="font-medium text-ok">Работает</span> — бот {s.botName}, режим{' '}
            {s.mode === 'webhook' ? 'вебхук' : 'опрос'}
            {s.lastActivityAt && (
              <span className="text-steel"> · последнее сообщение {formatMoment(s.lastActivityAt)}</span>
            )}
          </p>
        ) : (
          <p className="text-danger">Ошибка: {s.error ?? 'нет связи с сервером мессенджера'}</p>
        )}
      </div>
    </li>
  );
}

function Messengers() {
  const q = useQuery(messengersQuery);
  return (
    <section className="panel">
      <div className="border-b border-line px-5 py-3.5">
        <h2 className="font-semibold">Боты</h2>
        <p className="text-[13px] text-steel">
          Клиенты пишут боту компании — переписка идёт в CRM. Бот не может написать клиенту первым и не видит
          личные чаты менеджеров.
        </p>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <div className="px-5 py-3">
          <ErrorBox error={q.error} />
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {q.data.map((s) => (
            <MessengerRow key={s.channel} s={s} />
          ))}
        </ul>
      )}
      <p className="border-t border-line px-5 py-2.5 text-[12.5px] text-steel">
        {Object.values(MESSENGER_LABELS).join(' и ')}: обращения идут напрямую к серверам мессенджеров, без
        посредников.
      </p>
    </section>
  );
}
